import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type FormEvent } from "react";
import {
  AccountHead,
  AccountLink,
  AccountSection,
  goLogin,
  useAccountUi,
  ZoneSelect,
  type Zone,
} from "../../account/accountUi";
import { postJson, readView, useDocumentTitle, useHeadingFocus, useSpaLinks } from "../../config/common";
import { DraftGuard } from "../../config/team";
import { formatText } from "../../i18n/texts";
import { text, type TeamUi } from "../../team/teamText";
import { useFeedback } from "../../team/TeamUi";
import { OptionCards, TextField } from "../../users/formParts";
import { UsersError, UsersFrame } from "../../users/usersUi";

// Mi perfil (/portal/perfil): nombre, apellido y zona horaria propia (Perfil.razor), con el kit. La zona deja de ser un
// campo con una opción escondida «usar la de la empresa»: son dos tarjetas C08 a la vista (la de la empresa, con su nombre,
// u otra elegida con búsqueda). Debajo, la entrada a Seguridad y a Preferencias. El correo es el usuario y no se cambia aquí.

type View = {
  email: string;
  firstName: string;
  lastName: string;
  timezone: string | null;
  companyTimezone: string;
  companyTimezoneLabel: string;
  zones: Zone[];
};
type Field = "firstName" | "lastName" | "timezone";
type Draft = { firstName: string; lastName: string; zoneMode: "company" | "own"; timezone: string };
type Reply = { saved?: boolean; fields?: Partial<Record<Field, string>>; errorKey?: string };

const draftOf = (view: View): Draft => ({
  firstName: view.firstName,
  lastName: view.lastName,
  zoneMode: view.timezone ? "own" : "company",
  timezone: view.timezone ?? "",
});

export function ProfilePage({ locale }: { locale: string }) {
  const ui = useAccountUi(locale);
  const view = useQuery({
    queryKey: ["account", "profile"],
    queryFn: () => readView<View & { errorKey?: string }>("/api/portal/account/profile"),
    staleTime: 0,
  });
  useSpaLinks();
  useHeadingFocus(!!ui && !!view.data);
  useDocumentTitle(ui ? text(ui, "NAV_PROFILE") : null);
  if (!ui) return null;
  if (view.data?.status === 401) {
    goLogin();
    return null;
  }
  const ready = !!view.data || view.isError;
  const data = view.data?.status === 200 ? view.data.body : null;
  return (
    <UsersFrame ui={ui} ready={ready}>
      <AccountHead
        ui={ui}
        title={text(ui, "NAV_PROFILE")}
        sub={data ? formatText(text(ui, "ACC_SIGNIN_AS"), data.email) : undefined}
      />
      {data ? (
        <ProfileForm key={view.dataUpdatedAt} ui={ui} view={data} />
      ) : (
        <UsersError
          message={text(ui, "PROFILE_ERROR")}
          retryLabel={text(ui, "CFG1_RETRY")}
          onRetry={() => void view.refetch()}
        />
      )}
      <div className="rows cfg1-config-list acc-links" data-part="mas-de-tu-cuenta">
        <AccountLink
          href="/portal/seguridad"
          icon="shield-check"
          title={text(ui, "SEC_TITLE")}
          hint={text(ui, "ACC_SECURITY_HINT")}
        />
        <AccountLink
          href="/portal/preferencias"
          icon="settings"
          title={text(ui, "NAV_PREFS")}
          hint={text(ui, "ACC_PREFS_HINT")}
        />
      </div>
    </UsersFrame>
  );
}

function ProfileForm({ ui, view }: { ui: TeamUi; view: View }) {
  const T = (key: string) => text(ui, key);
  const queryClient = useQueryClient();
  const feedback = useFeedback();
  const [saved, setSaved] = useState(() => draftOf(view));
  const [draft, setDraft] = useState(saved);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  const dirty =
    draft.firstName !== saved.firstName ||
    draft.lastName !== saved.lastName ||
    draft.zoneMode !== saved.zoneMode ||
    (draft.zoneMode === "own" && draft.timezone !== saved.timezone);
  const set = (change: Partial<Draft>) => setDraft((current) => ({ ...current, ...change }));
  const clear = (field: Field) => setErrors((current) => ({ ...current, [field]: undefined }));

  // C05: el foco va al primer campo que falla.
  const focusField = (field: Field) =>
    requestAnimationFrame(() =>
      form.current?.querySelector<HTMLElement>(`[data-field="${field}"] :is(input,button)`)?.focus(),
    );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const found: Partial<Record<Field, string>> = {};
    if (!draft.firstName.trim()) found.firstName = T("FEEDBACK_FIRST_REQUIRED");
    if (!draft.lastName.trim()) found.lastName = T("FEEDBACK_LAST_REQUIRED");
    if (draft.zoneMode === "own" && !draft.timezone) found.timezone = T("ACC_TZ_REQUIRED");
    setErrors(found);
    setError(null);
    const first = (["firstName", "lastName", "timezone"] as Field[]).find((field) => found[field]);
    if (first) return focusField(first);
    setBusy(true);
    try {
      const reply = await postJson<Reply>("/api/portal/account/profile", {
        firstName: draft.firstName.trim(),
        lastName: draft.lastName.trim(),
        timezone: draft.zoneMode === "own" ? draft.timezone : null,
      });
      if (reply.status === 401) return goLogin();
      if (reply.status === 200 && reply.body?.saved) {
        const next = { ...draft, firstName: draft.firstName.trim(), lastName: draft.lastName.trim() };
        setSaved(next);
        setDraft(next);
        feedback.show(T("PROFILE_SAVED"));
        // El nombre de la cabecera y la zona del chat salen de la sesión: se vuelven a leer.
        void queryClient.invalidateQueries({ queryKey: ["shell"] });
        return;
      }
      const fields = reply.body?.fields ?? {};
      const issues = Object.fromEntries(Object.entries(fields).map(([field, key]) => [field, T(key ?? "")]));
      if (Object.keys(issues).length > 0) {
        setErrors(issues);
        focusField(Object.keys(issues)[0] as Field);
      } else setError(T(reply.body?.errorKey ?? "CP_ACTION_UNAVAILABLE"));
    } catch {
      setError(T("CP_ACTION_UNAVAILABLE"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form ref={form} className="tm-form tm-user-form acc-form" noValidate onSubmit={(event) => void submit(event)}>
      <DraftGuard dirty={dirty} L={T} />
      {error ? <UsersError message={error} /> : null}
      <section className="tm-inbox-section" data-part="nombre">
        <div className="tm-fields">
          <TextField
            id="profile-first"
            field="firstName"
            label={T("REG_FIRST")}
            value={draft.firstName}
            error={errors.firstName}
            maxLength={100}
            autoComplete="given-name"
            onChange={(firstName) => {
              set({ firstName });
              clear("firstName");
            }}
          />
          <TextField
            id="profile-last"
            field="lastName"
            label={T("REG_LAST")}
            value={draft.lastName}
            error={errors.lastName}
            maxLength={100}
            autoComplete="family-name"
            onChange={(lastName) => {
              set({ lastName });
              clear("lastName");
            }}
          />
        </div>
      </section>
      <AccountSection id="profile-zone-title" title={T("COMPANY_TZ")} line={T("ACC_TZ_LINE")} part="zona-horaria">
        <OptionCards
          labelledBy="profile-zone-title"
          name="zone"
          value={draft.zoneMode}
          onChange={(zoneMode) => {
            set({ zoneMode: zoneMode as Draft["zoneMode"] });
            clear("timezone");
          }}
          options={[
            {
              value: "company",
              title: T("ACC_TZ_COMPANY"),
              line: formatText(T("ACC_TZ_COMPANY_LINE"), view.companyTimezoneLabel),
            },
            { value: "own", title: T("ACC_TZ_OWN"), line: T("ACC_TZ_OWN_LINE") },
          ]}
        />
        {draft.zoneMode === "own" ? (
          <div className="tm-fields tm-fields-single acc-zone">
            <ZoneSelect
              ui={ui}
              id="profile-zone"
              label={T("ACC_TZ_PICK")}
              value={draft.timezone}
              zones={view.zones}
              error={errors.timezone}
              onChange={(timezone) => {
                set({ timezone });
                clear("timezone");
              }}
            />
          </div>
        ) : null}
      </AccountSection>
      <div className="tm-form-actions" data-fixed-actions="" data-part="acciones">
        <button type="submit" className="tm-btn primary" disabled={busy} aria-busy={busy ? "true" : undefined}>
          {T("ACTION_SAVE")}
        </button>
      </div>
    </form>
  );
}
